import { useState, useEffect, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useBoolean } from "@fluentui/react-hooks";
import { ServiceContext, IUser } from "@msx/platform-services";
import { DataServiceContext } from "../../../../../app/context";
import {
  getCurrentLocale,
  setCurrentLocale,
  getCurrentTheme,
  setCurrentTheme,
  fetchExtensionsRegistration,
} from "../../../../store";
import {
  fetchUserProfileBegin,
  fetchUserProfileSuccess,
  fetchNotifications,
} from "../../../../../app/store";
import { appConfig } from "../../../../../app/App.config";
import { getThemeByName } from "../../../../utils";

/**
 * useUserAuthorization — Manages user login state, authorization checks, and startup data loading.
 *
 * Extracted from V2_Shell.tsx (PR #3, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useState moved: isStartupError, startupError, isUserLoggedIn,
 *                 isUserAuthorized, isUserAuthorizedAi
 * Functions moved: loadStartupData, handleResize
 * Effects moved: Effect #2 (mount effect)
 */
export const useUserAuthorization = (params: {
  setCurentUser: (user: IUser) => void;
}) => {
  const { setCurentUser } = params;
  const { authClient } = useContext(ServiceContext);
  const { dataService } = useContext(DataServiceContext);
  const reduxDispatch = useDispatch();
  const currentLocale = useSelector(getCurrentLocale);
  const currentLocaleRef = useRef(currentLocale);
  const currentTheme = useSelector(getCurrentTheme);

  const [
    isStartupError,
    { setTrue: activateStartupError, setFalse: dismissStartupError },
  ] = useBoolean(false);
  const [startupError, setStartupError] = useState("");
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isUserAuthorized, setUserAuthorized] = useState(false);
  const [isUserAuthorizedAi, setUserAuthorizedAi] = useState(false);

  //data for 2 useeffect
  const loadStartupData = async () => {
    const isLoggedIn = await authClient.isLoggedIn();
    setIsUserLoggedIn(isLoggedIn);
    if (isLoggedIn) {
      const user = await authClient.getUser();
      setCurentUser(user);
    }

    if (!isLoggedIn) {
      return;
    }

    const Window = window as any;
    if (
      Window.hasStartupDataLoaded === true ||
      Window.hasStartupDataInprogress === true
    ) {
      return;
    }

    Window.hasStartupDataInprogress = true;
    try {
      dismissStartupError();
      // load user profile
      if (appConfig.enableUserSettingsApi) {
        reduxDispatch(fetchUserProfileBegin());
        const userProfile = await dataService.GetUserProfile();
        reduxDispatch(fetchUserProfileSuccess(userProfile));
        const locale = userProfile.userPreference.locale;
        if (locale !== currentLocaleRef.current) {
          reduxDispatch(setCurrentLocale(locale));
        }
        const themeName = userProfile.userPreference.theme;
        if (currentTheme.name !== themeName) {
          const newTheme = getThemeByName(themeName);
          reduxDispatch(setCurrentTheme(newTheme));
        }
      }

      // load notifications
      if (appConfig.notificaitonConfig.active) {
        reduxDispatch(fetchNotifications(dataService));
      }

      // load extension registraton
      if (appConfig.registrationConfig.active) {
        reduxDispatch(fetchExtensionsRegistration(dataService, []));
      }
      Window.hasStartupDataLoaded = true;
      Window.hasStartupDataInprogress = false;
    } catch (error) {
      Window.hasStartupDataInprogress = false;
      setStartupError(JSON.stringify(error));
      activateStartupError();
    }
  };

  const handleResize = (): void => {
    // do something
  };

  //2. useEffect
  useEffect(() => {
    //MOUNT
    sessionStorage.removeItem("activeTabKey");
    loadStartupData();
    window.removeEventListener("resize", handleResize);
    window.addEventListener("resize", handleResize);
    //UNMOUNT
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return {
    isStartupError,
    startupError,
    isUserLoggedIn,
    isUserAuthorized,
    setUserAuthorized,
    isUserAuthorizedAi,
    setUserAuthorizedAi,
  };
};
